import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { review } from '../api/methods';
export const reviewKeys = {
    queue: ['review-queue'],
    queueFiltered: (opts) => ['review-queue', opts],
    count: (opts) => ['review-queue', 'count', opts],
};
// Match every review-queue LIST cache (['review-queue'] and its filtered
// variants) but NOT the lightweight count cache (['review-queue','count',…]),
// which holds a number, not a queue response. Used to patch tickets optimistically.
function isQueueListQuery(query) {
    const key = query.queryKey;
    return Array.isArray(key) && key[0] === 'review-queue' && key[1] !== 'count';
}
/** Approve one flagged material inside a queued ticket (clears its review flag). */
function approveMaterialInTicket(t, materialId) {
    if (!t.materials?.some((m) => m.id === materialId && m.review_required))
        return t;
    return {
        ...t,
        materials: t.materials.map((m) => (m.id === materialId ? { ...m, review_required: false } : m)),
        flagged_material_count: Math.max(0, (t.flagged_material_count ?? 0) - 1),
    };
}
/** Apply a correction to one material inside a queued ticket. */
function correctMaterialInTicket(t, materialId, data) {
    if (!t.materials?.some((m) => m.id === materialId))
        return t;
    const wasFlagged = t.materials.some((m) => m.id === materialId && m.review_required);
    return {
        ...t,
        materials: t.materials.map((m) => m.id === materialId
            ? {
                ...m,
                qb_item_id: data.qb_item_id,
                sku_name_snapshot: data.sku_name_snapshot,
                quantity: data.quantity ?? m.quantity,
                unit_price: data.unit_price ?? m.unit_price,
                review_required: false,
            }
            : m),
        flagged_material_count: wasFlagged
            ? Math.max(0, (t.flagged_material_count ?? 0) - 1)
            : t.flagged_material_count ?? 0,
    };
}
export function useReviewQueue(options) {
    const includeImports = options?.includeImports ?? false;
    const shopKey = options?.shopKey ?? null;
    const listId = options?.listId ?? null;
    const hasFilters = Boolean(includeImports || shopKey || listId);
    const queryKey = hasFilters
        ? reviewKeys.queueFiltered({ includeImports, shopKey, listId })
        : reviewKeys.queue;
    const query = useQuery({
        queryKey,
        queryFn: () => review.getQueue(hasFilters ? { includeImports, shopKey, listId } : undefined),
        refetchInterval: 30_000,
        // The full queue is only mounted on the review page now; 30s of freshness is
        // plenty there and stops every focus/remount from refetching the whole list.
        staleTime: 30_000,
    });
    return {
        ...query,
        data: query.data?.tickets,
        lists: query.data?.lists ?? [],
        metadata: query.data?.metadata,
    };
}
/**
 * Actionable review-queue count for the nav badge. Polls the lightweight
 * count-only endpoint every 30s instead of dragging the full queue over the
 * wire on every page — the badge only ever needed the number.
 */
export function useReviewQueueCount(options) {
    const shopKey = options?.shopKey ?? null;
    return useQuery({
        queryKey: reviewKeys.count({ shopKey }),
        queryFn: () => review.getQueueCount(shopKey ? { shopKey } : undefined),
        refetchInterval: 30_000,
        staleTime: 30_000,
    });
}
export function useCorrectMaterial() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ materialId, data }) => review.correctMaterial(materialId, data),
        // Optimistically patch the corrected material in every queue-list cache so
        // the row updates instantly; reconcile in onSettled.
        onMutate: async ({ materialId, data }) => {
            await queryClient.cancelQueries({ queryKey: reviewKeys.queue });
            const previous = queryClient.getQueriesData({ predicate: isQueueListQuery });
            queryClient.setQueriesData({ predicate: isQueueListQuery }, (result) => result
                ? { ...result, tickets: result.tickets.map((t) => correctMaterialInTicket(t, materialId, data)) }
                : result);
            return { previous };
        },
        onError: (_err, _vars, ctx) => {
            for (const [key, data] of ctx?.previous ?? [])
                queryClient.setQueryData(key, data);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
        },
    });
}
export function useApproveMaterial() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (materialId) => review.approveMaterial(materialId),
        // Optimistically clear the material's review flag in every queue-list cache.
        onMutate: async (materialId) => {
            await queryClient.cancelQueries({ queryKey: reviewKeys.queue });
            const previous = queryClient.getQueriesData({ predicate: isQueueListQuery });
            queryClient.setQueriesData({ predicate: isQueueListQuery }, (result) => 
            // Tolerate a stale pre-#867 cache entry (bare ticket array) — never
            // throw inside onMutate or the optimistic update poisons the mutation.
            result && Array.isArray(result.tickets)
                ? { ...result, tickets: result.tickets.map((t) => approveMaterialInTicket(t, materialId)) }
                : result);
            return { previous };
        },
        onError: (_err, _materialId, ctx) => {
            for (const [key, data] of ctx?.previous ?? [])
                queryClient.setQueryData(key, data);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
        },
    });
}
export function useBulkApprove() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (ticketIds) => review.bulkApprove(ticketIds),
        // Optimistically drop the approved tickets from every queue-list cache so
        // they vanish immediately; reconcile in onSettled.
        onMutate: async (ticketIds) => {
            await queryClient.cancelQueries({ queryKey: reviewKeys.queue });
            const previous = queryClient.getQueriesData({ predicate: isQueueListQuery });
            const removing = new Set(ticketIds);
            queryClient.setQueriesData({ predicate: isQueueListQuery }, (result) => 
            // See useApproveMaterial: tolerate a stale pre-#867 array-shaped entry.
            result && Array.isArray(result.tickets)
                ? { ...result, tickets: result.tickets.filter((t) => !removing.has(t.id)) }
                : result);
            return { previous };
        },
        onError: (_err, _ticketIds, ctx) => {
            for (const [key, data] of ctx?.previous ?? [])
                queryClient.setQueryData(key, data);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: reviewKeys.queue });
        },
    });
}
//# sourceMappingURL=useReviewQueue.js.map